import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import Pagination from "../components/Pagination";
import { useAuth, refreshMe } from "../lib/useAuth";
import { showcasePath } from "../lib/slug";
import {
  fetchPublicProfile,
  listMySubmissions,
  listUserShowcases,
  setMyUsername,
} from "../lib/api";

/**
 * Helper — build the canonical showcase URL for a profile gallery
 * entry. Falls back to null for entries whose set is hidden or where
 * the set summary wasn't hydrated (defensive; shouldn't happen for
 * approved showcases). Callers must render a non-link card in that
 * case so the tile stays intact without dead navigation.
 */
function _profileShowcaseHref(entry) {
  if (!entry?.set?.name || !entry?.card?.name || !entry?.card?.number || !entry?.id) return null;
  if (entry.set.is_public === false || entry.set.is_active === false) return null;
  return showcasePath({
    game: "pokemon",
    lang: "en",
    setName: entry.set.name,
    cardName: entry.card.name,
    number: entry.card.number,
    variantSlug: entry.variant?.slug || entry.card_variant_slug || null,
    defaultVariantSlug: entry.default_variant_slug || null,
    submissionId: entry.id,
  });
}

function formatJoinDate(iso) {
  if (!iso) return "join date unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "join date unknown";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

export default function ProfilePage({ ownView = false }) {
  const params = useParams();
  const auth = useAuth();
  const [profile, setProfile] = useState(null);
  const [subsData, setSubsData] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [galleryPage, setGalleryPage] = useState(1);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  const targetId = ownView ? auth.user?.id : params.userId;
  const displayUser = ownView && auth.user ? auth.user : null;
  // Owner report 2026-02-01: users hitting their OWN public URL
  // (/users/{their-id}) got no editor because App.js passes
  // ownView=false for that route. Detect self and treat it as own view
  // even when the flag prop is false — server always gates writes on
  // the JWT anyway.
  const effectiveOwnView =
    ownView || (!!auth.user?.id && auth.user.id === params.userId);

  useEffect(() => {
    let alive = true;
    if (auth.loading) return;
    if (ownView && !auth.user) { setState("unauth"); return; }
    if (!targetId) { setState("error"); setError("No user id provided"); return; }

    setState("loading");
    fetchPublicProfile(targetId)
      .then((p) => { if (alive) { setProfile(p); setState("ready"); } })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load");
        setState("error");
      });
    // My-entries section is gated on effectiveOwnView (declared just
    // above) — reuse it so the fetch gate can't drift from the
    // rendered gate.
    if (effectiveOwnView) {
      listMySubmissions()
        .then((d) => { if (alive) setSubsData(d); })
        .catch(() => {});
    }
    // Public approved-entries gallery (available for own view too — see
    // the "Approved" list is already covered by the ownView block, but
    // the public gallery is separately paginated).
    listUserShowcases(targetId, { page: galleryPage, pageSize: 24 })
      .then((d) => { if (alive) setGallery(d); })
      .catch(() => { if (alive) setGallery(null); });
    return () => { alive = false; };
  }, [targetId, ownView, auth.loading, auth.user, galleryPage, effectiveOwnView]);

  if (state === "unauth") {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-5 py-24 md:px-gutter" data-testid="profile-unauth">
        <h1 className="text-title-page text-ink-text">Sign in required</h1>
        <p className="mt-2 max-w-[46ch] text-body text-ink-muted">
          Your profile is private. Sign in with Google (or, in preview,
          mint a TEST_MODE session — see{" "}
          <Link to="/" className="text-indigo-text hover:underline">Home</Link>)
          to see your entries.
        </p>
      </div>
    );
  }

  const submissions = subsData?.submissions || [];
  const counts = subsData?.counts || { pending: 0, approved: 0, rejected: 0 };
  const byStatus = {
    pending: submissions.filter((s) => s.status === "pending"),
    approved: submissions.filter((s) => s.status === "approved"),
    rejected: submissions.filter((s) => s.status === "rejected"),
  };

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid={ownView ? "own-profile-page" : "public-profile-page"}
    >
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: ownView ? "My profile" : profile?.display_name || "Collector" },
        ]}
      />

      {state === "loading" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="profile-loading">Loading profile…</div>
      )}
      {state === "error" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="profile-error">{error}</div>
      )}

      {profile && (
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <section>
            <div className="flex items-center gap-4">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt=""
                  className="h-16 w-16 rounded-full object-cover"
                  data-testid="profile-avatar"
                />
              ) : (
                <div
                  data-testid="profile-avatar-placeholder"
                  className="flex h-16 w-16 items-center justify-center rounded-full border border-ink-chip bg-ink-raised text-title-section text-ink-muted"
                >
                  {(profile.display_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1
                  className="flex items-center gap-2 text-title-page text-ink-text"
                  data-testid="profile-display-name"
                >
                  <span>{profile.username || profile.display_name}</span>
                  {effectiveOwnView && (
                    <button
                      type="button"
                      data-testid="username-edit-icon"
                      onClick={() => {
                        const el = document.querySelector('[data-testid="username-editor"]');
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                        setTimeout(() => {
                          document.querySelector('[data-testid="username-input"]')?.focus();
                        }, 300);
                      }}
                      aria-label={profile.username ? "Edit username" : "Choose a username"}
                      title={profile.username ? "Edit username" : "Choose a username"}
                      className="rounded-chip border border-transparent p-1 text-ink-muted transition-colors duration-cc ease-cc-ease hover:border-ink-chip hover:text-ink-text"
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                </h1>
                {profile.username && (
                  <div className="mt-0.5 text-meta text-ink-muted" data-testid="profile-display-name-secondary">
                    {profile.display_name}
                  </div>
                )}
                <div className="mt-1 text-meta text-ink-muted tabular" data-testid="profile-join">
                  Joined {formatJoinDate(profile.created_at)}
                </div>
              </div>
            </div>

            {effectiveOwnView && (
              <UsernameEditor
                current={profile.username}
                onSaved={(nextUser) => {
                  refreshMe();
                  window.dispatchEvent(new Event("cc-auth-changed"));
                  setProfile((p) => (p ? { ...p, username: nextUser.username } : p));
                }}
              />
            )}

            {effectiveOwnView && (
              <div className="mt-10" data-testid="my-entries">
                <div className="text-eyebrow uppercase text-ink-muted">My entries</div>
                <div className="mt-2 flex items-baseline gap-6 text-meta tabular">
                  <StatusPill label="Pending" count={counts.pending} testId="my-pending-count" />
                  <StatusPill label="Approved" count={counts.approved} testId="my-approved-count" />
                  <StatusPill label="Rejected" count={counts.rejected} testId="my-rejected-count" />
                </div>

                <div className="mt-6 space-y-8">
                  <EntryList
                    title="Pending"
                    items={byStatus.pending}
                    empty="Nothing pending right now."
                    testId="my-pending-list"
                  />
                  <EntryList
                    title="Approved"
                    items={byStatus.approved}
                    empty="No approved showcases yet."
                    testId="my-approved-list"
                  />
                  <EntryList
                    title="Rejected"
                    items={byStatus.rejected}
                    empty="Nothing rejected. Nice."
                    testId="my-rejected-list"
                  />
                </div>
              </div>
            )}

            {!effectiveOwnView && (
              <div className="mt-10" data-testid="public-approved-gallery">
                <div className="text-eyebrow uppercase text-ink-muted">Approved showcases</div>
                {gallery && gallery.results.length > 0 ? (
                  <>
                    <ul className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                      {gallery.results.map((entry) => {
                        const href = _profileShowcaseHref(entry);
                        // Owner spec: profile tiles NAVIGATE to the showcase
                        // page (unlike card-page tiles which open lightbox).
                        const Wrapper = href ? Link : "div";
                        const wrapperProps = href
                          ? {
                              to: href,
                              "data-testid": `public-gallery-entry-${entry.id}`,
                              className:
                                "block overflow-hidden rounded-card border border-ink-chip bg-ink-surface transition-colors duration-cc ease-cc-ease hover:border-ink-muted",
                            }
                          : {
                              "data-testid": `public-gallery-entry-${entry.id}`,
                              className:
                                "block overflow-hidden rounded-card border border-ink-chip bg-ink-surface opacity-70",
                            };
                        return (
                          <li key={entry.id}>
                            <Wrapper {...wrapperProps}>
                            <div className="relative" style={{ aspectRatio: "5 / 7" }}>
                              {entry.image?.thumbnail_url || entry.image?.url ? (
                                <img
                                  src={entry.image.thumbnail_url || entry.image.url}
                                  alt={
                                    entry.card?.name
                                      ? `${entry.card.name} ${entry.card.number || ""}${entry.variant?.variant_label ? ` ${entry.variant.variant_label}` : ""} — collector showcase`
                                      : "collector showcase"
                                  }
                                  loading="lazy"
                                  className="h-full w-full object-cover"
                                />
                              ) : entry.video?.poster_url ? (
                                <img src={entry.video.poster_url} alt={
                                    entry.card?.name
                                      ? `${entry.card.name} ${entry.card.number || ""}${entry.variant?.variant_label ? ` ${entry.variant.variant_label}` : ""} — collector showcase`
                                      : "collector showcase"
                                  } className="h-full w-full object-cover" />
                              ) : (
                                <div className="cc-card-plate h-full w-full">no media</div>
                              )}
                            </div>
                            <div className="p-3">
                              <div className="truncate text-card-name text-ink-text">
                                {entry.card?.name || entry.card_external_id}
                                {entry.variant?.variant_label && (
                                  <span className="ml-1 text-ink-muted"> · {entry.variant.variant_label}</span>
                                )}
                              </div>
                              <div className="text-meta text-ink-muted tabular">
                                #{entry.card?.number || "?"} · {entry.votes?.up || 0} upvotes
                              </div>
                            </div>
                            </Wrapper>
                          </li>
                        );
                      })}
                    </ul>
                    <Pagination
                      page={gallery.page}
                      totalPages={gallery.total_pages}
                      onChange={(n) => setGalleryPage(n)}
                      testId="public-gallery-pagination"
                    />
                  </>
                ) : (
                  <>
                    <h2 className="mt-1 text-title-section text-ink-text">Nothing to show yet</h2>
                    <p className="mt-2 max-w-[62ch] text-body text-ink-muted">
                      This collector hasn't had any submissions approved yet.
                    </p>
                  </>
                )}
              </div>
            )}
          </section>

          <aside className="lg:sticky lg:top-[92px]">
            <div className="rounded-card border border-ink-chip bg-ink-surface p-5">
              <div className="text-eyebrow uppercase text-ink-muted">Stats</div>
              <dl className="mt-4 space-y-3">
                <StatRow label="Total upvotes received" value={profile.total_upvotes} testId="stat-upvotes" />
                <StatRow label="Approved entries" value={profile.approved_entries} testId="stat-approved" />
              </dl>
            </div>

            {ownView && displayUser?.is_admin && (
              <div className="mt-4 rounded-card border border-indigo-chip-border bg-indigo-chip-bg p-4 text-meta text-indigo-chip-text">
                You're signed in as an admin. Admin tools land with moderation in a later phase.
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function StatusPill({ label, count, testId }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-title-section text-ink-text tabular" data-testid={testId}>{count}</span>
      <span className="text-eyebrow uppercase text-ink-muted">{label}</span>
    </div>
  );
}

function EntryList({ title, items, empty, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-title-section text-ink-text">{title}</div>
      {items.length === 0 ? (
        <p className="mt-2 text-body text-ink-muted">{empty}</p>
      ) : (
        <ul className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((s) => (
            <li key={s.id}>
              <Link
                to={`/cards/${s.card_external_id}/${s.card_variant_slug || s.variant?.slug || "normal"}`}
                data-testid={`my-entry-${s.card_external_id}-${s.card_variant_slug || s.variant?.slug || "normal"}`}
                className="flex gap-3 rounded-card border border-ink-chip bg-ink-surface p-3 transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
              >
                <div className="w-[56px] shrink-0 overflow-hidden rounded-chip">
                  {s.image?.thumbnail_url ? (
                    <img src={s.image.thumbnail_url} alt="" className="w-full object-cover" style={{ aspectRatio: "1/1" }} />
                  ) : s.video?.poster_url ? (
                    <img src={s.video.poster_url} alt="" className="w-full object-cover" style={{ aspectRatio: "1/1" }} />
                  ) : (
                    <div className="w-full bg-ink-bg" style={{ aspectRatio: "1/1" }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-card-name text-ink-text">
                    {s.card?.name || s.card_external_id}
                    {s.variant?.variant_label && (
                      <span className="ml-1 text-ink-muted"> · {s.variant.variant_label}</span>
                    )}
                  </div>
                  <div className="text-meta text-ink-muted tabular">#{s.card?.number || "?"}</div>
                  {s.video && s.video.transcode_status !== "ready" && (
                    <div className="mt-1 text-meta text-indigo-text">
                      video: {s.video.transcode_status}
                    </div>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-meta text-ink-muted">{label}</dt>
      <dd data-testid={testId} className="text-title-section text-ink-text tabular">{value}</dd>
    </div>
  );
}

function UsernameEditor({ current, onSaved }) {
  const [value, setValue] = useState(current || "");
  const [state, setState] = useState("idle");   // idle | saving | ok | err
  const [error, setError] = useState(null);
  useEffect(() => { setValue(current || ""); }, [current]);
  const submit = (e) => {
    e.preventDefault();
    setState("saving");
    setError(null);
    setMyUsername(value.trim())
      .then((u) => {
        setState("ok");
        onSaved?.(u);
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (Array.isArray(detail) ? detail[0]?.msg : null) || "Could not save username");
        setState("err");
      });
  };
  return (
    <form
      onSubmit={submit}
      className="mt-8 max-w-[520px] rounded-card border border-ink-chip bg-ink-surface p-4"
      data-testid="username-editor"
    >
      <label htmlFor="username-input" className="text-eyebrow uppercase text-ink-muted">
        {current ? "Change username" : "Choose a username"}
      </label>
      <p className="mt-1 max-w-[46ch] text-meta text-ink-muted">
        3-20 characters. Letters, digits, underscore, dot or dash. Case-insensitive; must be unique.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          id="username-input"
          data-testid="username-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. charizard_fan"
          className="min-w-[240px] flex-1 rounded-chip border border-ink-chip bg-ink-raised px-3 py-2 text-body text-ink-text placeholder:text-ink-muted focus:border-ink-muted focus:outline-none"
          maxLength={20}
        />
        <button
          type="submit"
          data-testid="username-save"
          disabled={state === "saving" || !value.trim() || value.trim() === (current || "")}
          className="rounded-chip border border-ink-chip bg-ink-raised px-4 py-2 text-meta text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted disabled:cursor-not-allowed disabled:opacity-60"
        >
          {state === "saving" ? "Saving…" : "Save"}
        </button>
      </div>
      {state === "err" && (
        <div className="mt-3 text-meta text-red-400" data-testid="username-error">{error}</div>
      )}
      {state === "ok" && (
        <div className="mt-3 text-meta text-emerald-400" data-testid="username-ok">Saved.</div>
      )}
    </form>
  );
}
